import type { EnumOption, StaticOptions } from '@resource-kit/model';

/**
 * The two members `resolveEnumItems` reads.
 *
 * Structural on purpose (#2167 PR-3, invariant 7). The resolver used to take
 * `EntityField<T>`, which is a REACT contract — it carries `render`,
 * `ComponentType`-typed — and naming it here would put `react/` inside the root
 * export's closure and make `import '@resource-kit/controllers'` a framework
 * binding. Both member types are copied verbatim from `EntityField`'s members
 * of the same name (`react/contracts.ts`), not widened, so every `EntityField`
 * still satisfies this and no call site changed. Same device as
 * `FilterTypeSource` next door.
 */
export interface EnumItemsSource {
  options?: StaticOptions;
  enumValues?: EnumOption[];
}

/**
 * Resolve a field's enum options to a concrete list, NEVER undefined.
 *
 * `StaticOptions.items` is typed non-optional, but a prod schema config
 * shipped a static field whose `items` was undefined at runtime; the
 * unguarded `.length`/`.map` in the form renderer and the column-meta
 * builder then crashed the dispatch, delivery-job and menu pages into the
 * ErrorBoundary (#880).
 * The `?? []` collapses that type-lie. Both ResourceFormDialog and
 * schema-utils route through this single resolver so the never-undefined
 * guarantee holds class-wide rather than per-callsite.
 *
 * Non-static fields (remote-source or plain enum columns) fall back to
 * the inline `enumValues` table; absent there too, the result is `[]`.
 * All `enumValues` consumers are length/`type==='enum'` gated, so `[]`
 * and `undefined` are equivalent to them — returning `[]` is the
 * stronger, crash-proof contract.
 */
export function resolveEnumItems(field: EnumItemsSource): EnumOption[] {
  if (field.options?.source === 'static') return field.options.items ?? [];
  return field.enumValues ?? [];
}
